import { useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTable, usePagination, useSortBy } from "react-table";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { dispatch } from "../../../state/store";
import { setSelectedConsentElement } from "../../../state/ducks";

import { routePaths, strCode } from "../../../consts";
import { useTableData } from "../../../hooks";
import { t } from "../../../services";

import {
  CircleIconButton,
  DndTable,
  FlexContainers,
  Paragraph2,
  SVG,
  Table,
  WrapperContainer
} from "../../../components";
import { ConsentElementsTableColumns } from "./ConsentElementsTableColumns";
import { EditConsentElementsTableColumns } from "./EditConsentElementsTableColumns";
import { ConsentTableWrapper } from "./styled";

const ConsentElementsTable = ({ consentElementsData, isViewOnly, setConsentElementsData }) => {
  const history = useHistory();

  const loading = useSelector((store) => store.consents?.loading);
  const isDuplicate = useSelector((store) => store.consents?.isDuplicate);
  const consentElementsVisibility = useSelector(
    (store) => store.consents.consentElementsVisibility
  );

  const { appName } = useParams();

  const data = useTableData({ loading, data: consentElementsData, loadingPlaceholdersRows: 5 });

  const columns = useMemo(
    () => (isViewOnly ? ConsentElementsTableColumns : EditConsentElementsTableColumns),
    [isViewOnly]
  );

  const isAllElementsVisible = useMemo(() => {
    return !consentElementsData.some((element) => !element.isConsentVisible);
  }, [consentElementsData]);

  const tableInstance = useTable(
    {
      columns,
      data
    },
    useSortBy,
    usePagination
  );

  const onAddNewConsentElement = () => {
    history.push(`${routePaths.APPS}/${appName}${routePaths.CONSENT_ELEMENT_CREATE}`);
  };

  const onSeeDetails = (row) => {
    if (row?.id !== undefined) {
      dispatch(setSelectedConsentElement(row));
      history.push(`${routePaths.APPS}/${appName}${routePaths.CONSENT_ELEMENT_EDIT}/${row.id}`);
    }
  };

  return (
    <ConsentTableWrapper mt={24} width="100%">
      {consentElementsData &&
        (isViewOnly ? (
          <Table
            tableInstance={tableInstance}
            isLoading={loading || (!loading && isDuplicate && !consentElementsData.length)}
            onRowClick={onSeeDetails}
            tooltipLabel={t(strCode.VIEW_DETAILS)}
            modalTable
            maxVisibleRows={1}
          />
        ) : (
          <DndTable
            data={consentElementsData}
            setData={setConsentElementsData}
            tableInstance={tableInstance}
            onRowClick={onSeeDetails}
            modalTable
            maxVisibleRows={1}
            isLoading={loading || (!loading && isDuplicate && !consentElementsData.length)}
            component={
              <WrapperContainer
                Container={FlexContainers.FlexColumnCenter}
                gap={24}
                mv={consentElementsData?.length || loading ? 10 : 24}>
                {!consentElementsData?.length && !loading && (
                  <Paragraph2>{t(strCode.NO_CONSENT_ELEMENTS_DATA)}</Paragraph2>
                )}

                <CircleIconButton
                  onClick={onAddNewConsentElement}
                  tooltipLabel={t(strCode.ADD_NEW_ELEMENT_LABEL)}
                  disabled={consentElementsVisibility === false || !isAllElementsVisible}>
                  <SVG.PlusIcon />
                </CircleIconButton>
              </WrapperContainer>
            }
            componentHeight={112}
            maxTableHeight={"88px"}
            tooltipLabel={t(strCode.EDIT)}
          />
        ))}
    </ConsentTableWrapper>
  );
};

ConsentElementsTable.propTypes = {
  consentElementsData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      position: PropTypes.number.isRequired,
      code: PropTypes.string.isRequired,

      consentFlags: PropTypes.arrayOf(PropTypes.string).isRequired,
      createdAtTs: PropTypes.number.isRequired,
      updatedAtTs: PropTypes.number,
      groupName: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,

      texts: PropTypes.arrayOf(
        PropTypes.shape({
          languageCode: PropTypes.string.isRequired,
          content: PropTypes.string.isRequired
        })
      )
    })
  ).isRequired,
  isViewOnly: PropTypes.bool,
  setConsentElementsData: PropTypes.func
};

export default ConsentElementsTable;
